/* eslint-disable @typescript-eslint/no-explicit-any */
import { ThunkMiddleware } from 'redux-thunk';
import {
	DONE_SAVE_VPC,
	SUCCESS_ADD_TO_BAG,
	SUCCESS_ADD_TO_BAG_YMAL,
	SET_APP_VIEW,
	SET_CURRENT_LIGHTBOX,
	SWIPE_IN_MODULE_SWIPER,
	APP_INIT,
	SET_FROM_NAVIGATOR,
	SET_PRODUCT_CARD_TYPE_CLICK,
	NO_ARTICLES_IN_PLANNER,
	THUMBNAIL_IMAGE_CHANGED,
	STATISTICS_QR_PLANNING_ENABLED,
} from '../application/types';
import { SET_DRAG_ITEM_ID, SET_SELECTED_ITEM_ID, SET_SWIPER_EVENT_TYPE } from '../swiper/types';
import {
	SET_SELECTED_COVER,
	SHOW_COVER_PRODUCT_DESCRIPTION,
	SET_COVER_ONLY,
	ADD_ADDITIONAL_COVER,
	ADDITIONAL_COVER_ITEM_CLICKED,
	ADDITIONAL_COVER_CLOSED,
	ADDITIONAL_COVER_ITEM_REMOVED,
} from '../cover/types';
import { SET_SHOW_OPEN_DESIGN } from '../openDesign/types';
import { Actions, RootState } from '..';
import { localeService, statisticsService, UrlService } from '../../services';
import {
	SET_CLOSE_GALLERY,
	SET_GALLERY_PREDEFINED_CONFIGURATION_ID,
	SET_MISSING_DEEPLINK,
} from '../sofaConfiguration';
import {
	SET_SHOW_FUNCTION_SLEEP,
	SET_SHOW_FUNCTION_STORAGE,
	SET_SHOW_FUNCTIONS,
	SET_SHOW_MEASUREMENTS,
	SET_ROTATION_BUTTON_CLICKED,
	SET_SHOW_SELECT_ALL,
} from '../actionButtons';
import {
	FETCH_VPC_SUCCESS,
	YMAL_PIP_LINK_CLICK,
	SUMMARY_PAGE_TAB_CLICK,
	SET_VISIBLE_MODAL,
	SEND_SMS,
	SEND_EMAIL,
	COPY_CODE,
	SAVE_FOR_LATER_TAB_CLICK,
	SUMMARY_PAGE_EXPAND_PRODUCT,
	SHOW_ADD_TO,
	SET_UPPTACKA_SAVE_FAIL,
	SORT_PRODUCT_LIST,
	SET_SUMMARY_PAGE_DEFAULT_TAB,
	BACK_TO_SHARE_MENU,
} from '../../store/checkout/types';
import { setFeatures } from '../settings';
import {
	Settings,
	Platform,
	RangeSettings,
	PredefinedConfiguration,
	SofaRangeName,
	PopOverType,
	ShoppingItem,
} from 'sofa-reborn-react';
import { SET_ORIENTATION } from '../deviceInfo/types';
import {
	SET_SCENE_DATA,
	REMOVE_SCENE_ITEM,
	REMOVE_SCENE_ITEM_OUTSIDE_SCENE,
	MOVE_BACKREST_AS_ARMREST,
} from '../scene/types';
import {
	ProductService,
	KompisDefaultFields,
	CheckoutAvailabilityService,
} from '@inter-ikea-kompis/services';
import { IpexMomentEnum, DesignSourceEnum } from '@insights/insights-data-provider';
import { SET_SELECTED_ARMREST } from '../armrest';
import { SET_NO_ROTATION_ICON } from '../scene';
import { ProductUtils } from '../../util/ProductUtils';
import { SHOW_SCREENSAVER, SHOW_TIMEOUT_WARNING } from '../screensaver';
import { VisibleModal } from 'sofa-reborn-react';
import { SET_ZIP_CODE, SET_STORE_ID } from '../../store/localization/types';
import { initiateInsights } from '../application/actions';
import { AllRangeNames, PopOverEnum, ProductType } from '../../typings/enums';
import { SHOW_POP_OVER } from '../popOver';

let vpcCode: string | null = null;
let hasSentRotateEvent = false;
let hasSentSleepEvent = false;
let hasSentStorageEvent = false;
let hasSentPopoverEvent = false;
let hasSentFromNavigator = false;
let user: any = null;

async function initializeStatistics(
	settings: Settings,
	platform: Platform,
	persistedConfiguration: string | null,
	rangeSettings: RangeSettings,
	predefinedConfigurations: PredefinedConfiguration[],
	features: string[] | null,
	dispatch: any,
	designSource?: DesignSourceEnum
): Promise<void> {
	const predefinedConfigurationId: string | null = UrlService.getVpcOrSprId();

	// Initiate once we have downloaded settings from översätta
	await statisticsService.initStatistics(
		settings,
		platform,
		rangeSettings.rangeName as SofaRangeName,
		(optimizelyClient) => {
			setFeature(optimizelyClient, features, dispatch);
		}
	);

	if (predefinedConfigurationId) {
		if (!persistedConfiguration) {
			const isVpc = predefinedConfigurationId.length <= 6;

			if (isVpc && designSource) {
				statisticsService.sendInitPlanningSessionInsightEvent(
					predefinedConfigurationId,
					designSource,
					settings,
					platform,
					rangeSettings.rangeName as SofaRangeName
				);
			} else {
				const predefinedConfiguration = predefinedConfigurations.find(
					(configuration) =>
						configuration.id === predefinedConfigurationId ||
						configuration.localId === predefinedConfigurationId
				);
				if (predefinedConfiguration) {
					statisticsService.sendInitPlanningSessionInsightEvent(
						predefinedConfigurationId,
						DesignSourceEnum.spr,
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName
					);
				}
			}
		}
		dispatch(initiateInsights(true));
	}
}

function setFeature(optimizelyClient: any, features: string[] | null, dispatch: any): void {
	const abVersionName = 'moveymal'; // Changes abVersionName to what feature you wish to A/B test.

	let userId = statisticsService.getOptimizelyCookie();
	if (!userId) {
		userId = statisticsService.generateOptimizelyUserId();
		statisticsService.setOptimizelyCookie(userId);
	}

	user = optimizelyClient.createUserContext(userId);
	const decision = user?.decide(abVersionName, true).enabled;

	if (decision) {
		const updatedFeatures = features ? features.concat([abVersionName]) : [abVersionName];
		dispatch(setFeatures(updatedFeatures));
	}
	statisticsService.setABVersion(abVersionName, decision ? 'enabled' : 'disabled');
}

function toSofaRangeName(sofaRange: string): SofaRangeName {
	const indexOf = Object.keys(AllRangeNames).indexOf(sofaRange);
	return Object.values(AllRangeNames)[indexOf] as unknown as SofaRangeName;
}

async function getOrderItems(
	settings: Settings,
	storeId: string,
	shoppingItems: ShoppingItem[]
): Promise<string[] | null> {
	const orderItems: string[] = [];
	await Promise.all(
		shoppingItems.map(async (item) => {
			if (await isOrderItem(settings, storeId, item)) {
				orderItems.push(item.id);
			}
		})
	);
	return orderItems.length > 0 ? orderItems : null;
}

async function isOrderItem(
	settings: Settings,
	storeId: string,
	shoppingItem: ShoppingItem
): Promise<boolean> {
	const checkoutAvailabilityService = new CheckoutAvailabilityService({
		locale: settings.locale,
		localizationEndpoints: settings.localizationEndpoints,
	});
	const storeAvailability = await checkoutAvailabilityService.getStoreAvailabilities(storeId, [
		shoppingItem,
	]);
	const content =
		(storeAvailability && storeAvailability.length > 0 && storeAvailability[0].content) || null;

	return (content && !content.cashCarry?.inRange && content.homeDelivery?.inRange) || false;
}

const statisticsMiddleware: ThunkMiddleware<RootState, Actions> =
	(api) =>
	(next) =>
	async (action: Actions): Promise<Actions> => {
		const prevState = api.getState();
		const dispatch = api.dispatch;
		const settings = prevState.settings.settings;
		const previousOrientation = prevState.deviceInfo.orientation;
		const result = next(action);
		const nextState = api.getState();
		const currentLightbox = nextState.application.currentLightbox;
		const deviceType = nextState.deviceInfo.deviceType;
		const missingCover = nextState.cover.missingCover;
		const platform = nextState.deviceInfo.platform;
		const rangeSettings = nextState.settings.rangeSettings;
		const persistedConfiguration = nextState.sofaConfiguration.persistedConfiguration;
		const predefinedConfigurations = nextState.settings.predefinedConfigurations;
		const predefinedConfigurationId = nextState.sofaConfiguration.predefinedConfigurationId;
		const features = nextState.settings.features;
		const isInUpptackaPlatform = nextState.application.isInUpptackaPlatform;
		const designSource = nextState.checkout.designSource?.source;
		const insightsInitiated = nextState.application.insightsInitiated;
		const isScreensaverShown = nextState.screensaver.showScreensaver;
		const selectedArticle = nextState.scene.selectedSceneArticle;

		switch (action.type) {
			case APP_INIT: {
				initializeStatistics(
					settings,
					platform,
					persistedConfiguration,
					rangeSettings as RangeSettings,
					predefinedConfigurations,
					features,
					dispatch,
					designSource
				).then(() => {
					if (rangeSettings) {
						const ikeaPlusCoverItems = rangeSettings.coverItems.filter((cover) => cover.isIkeaPlus);

						const marketHasIkeaPlus = api.getState().cover.coverItems.find((coverItem) => {
							return ikeaPlusCoverItems.find(
								(ikeaPlusCoverItem) => ikeaPlusCoverItem.coverName === coverItem.coverName
							);
						});

						if (marketHasIkeaPlus) {
							statisticsService.sendCustomInsightEvent(
								'ikea_plus_available',
								settings,
								platform,
								rangeSettings.rangeName as SofaRangeName,
								IpexMomentEnum.nonInteraction
							);
						}
					}
				});

				break;
			}
			case SUCCESS_ADD_TO_BAG: {
				// Send analytics event (Shopping list and add to bag)
				const bagType = action.value.type.includes('Cart')
					? 'Cart'
					: action.value.type.includes('List')
					? 'List'
					: 'Bag';

				// Optimizely tracking
				if (user) {
					user.trackEvent('give_me');
				}

				const shoppingItems = api.getState().sofaConfiguration.shoppingItems;
				const additionalCoverItems = api.getState().cover.additionalCoverItems;
				const selectedCover = api.getState().cover.selectedCover;
				const isCoverOnly = api.getState().cover.coverOnly;
				const twoSeparateSofas = api.getState().checkout.twoSeparateSofas;
				const storeId = api.getState().localization.storeId;
				const items = [...shoppingItems, ...additionalCoverItems];

				const products = api.getState().products.products;
				const models = api.getState().models.sofaModels;
				const rangeSettings = api.getState().settings.rangeSettings;
				let productType: string | null = null;
				if (rangeSettings) {
					const hasFunctionSleep = shoppingItems.some((item) =>
						ProductUtils.hasFeatureSleep(products, models, item.id, rangeSettings)
					);
					const hasFunctionStorage = shoppingItems.some((item) =>
						ProductUtils.hasFeatureStorage(products, models, item.id, rangeSettings)
					);
					productType =
						hasFunctionSleep && hasFunctionStorage
							? ProductType.SleepStorage
							: hasFunctionSleep
							? ProductType.Sleep
							: hasFunctionStorage
							? ProductType.Storage
							: ProductType.None;
				}

				const productService = new ProductService({
					locale: settings.locale,
					localizationPrefixApplication: settings.localizationPrefixApplication,
					localizationEndpoints: settings.localizationEndpoints,
				});
				const shoppingProducts = await productService.getShoppingProducts(
					items,
					KompisDefaultFields
				);
				const isIkeaPlus = selectedCover?.isIkeaPlus || false;
				const orderItems =
					storeId && !isIkeaPlus ? await getOrderItems(settings, storeId, items) : null;
				if (rangeSettings) {
					statisticsService.sendAddToBagInsightEvent(
						bagType,
						shoppingProducts,
						items,
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						true,
						vpcCode,
						productType,
						null,
						selectedCover?.isIkeaPlus || false,
						isCoverOnly || false,
						action.hasAdditionalCover || false,
						twoSeparateSofas || false,
						orderItems
					);

					if (action.hasAdditionalCover) {
						statisticsService.sendCustomInsightEvent(
							'additional_cover_give_me',
							settings,
							platform,
							rangeSettings.rangeName as SofaRangeName,
							IpexMomentEnum.giveMe,
							{ type: bagType }
						);
					}
				}
				break;
			}
			case SUCCESS_ADD_TO_BAG_YMAL: {
				const bagType = action.value.type;
				const sectionType = action.value.sectionType;

				const shoppingItems = action.value.shoppingItems;
				const productService = new ProductService({
					locale: settings.locale,
					localizationPrefixApplication: settings.localizationPrefixApplication,
					localizationEndpoints: settings.localizationEndpoints,
				});
				const products = await productService.getShoppingProducts(
					shoppingItems,
					KompisDefaultFields
				);
				if (rangeSettings) {
					statisticsService.sendAddToBagInsightEvent(
						bagType,
						products,
						shoppingItems,
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						false,
						null,
						null,
						sectionType
					);
				}
				break;
			}
			case DONE_SAVE_VPC: {
				// Send analytics event (Save vpc)
				vpcCode = action.value;
				const twoSeparateSofas = api.getState().checkout.twoSeparateSofas;
				const storeId = api.getState().localization.storeId;
				const shoppingItems = api.getState().sofaConfiguration.shoppingItems;
				const selectedCover = api.getState().cover.selectedCover;

				if (rangeSettings) {
					const isIkeaPlus = selectedCover?.isIkeaPlus || false;
					const orderItems =
						storeId && !isIkeaPlus ? await getOrderItems(settings, storeId, shoppingItems) : null;

					statisticsService.saveDesignInsightEvent(
						vpcCode as string,
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						twoSeparateSofas || false,
						orderItems
					);
				}

				if (user) {
					user.trackEvent('give_me');
				}

				break;
			}
			case FETCH_VPC_SUCCESS: {
				vpcCode = action.vpcCode;

				if (vpcCode && rangeSettings) {
					statisticsService.sendVpcInteractionEvent(
						vpcCode,
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName
					);
				}
				break;
			}
			case SET_GALLERY_PREDEFINED_CONFIGURATION_ID: {
				if (!insightsInitiated) {
					const configurationId = action.value;
					if (configurationId.length > 6) {
						const predefinedConfiguration = predefinedConfigurations.find(
							(configuration) => configuration.id === configurationId
						);
						vpcCode = predefinedConfiguration
							? predefinedConfiguration.vpcCode || 'no_code'
							: 'no_code';
					} else {
						vpcCode =
							configurationId === 'blank' ? DesignSourceEnum.startFromScratch : configurationId;
					}
					const vpcSource =
						configurationId === 'blank'
							? DesignSourceEnum.startFromScratch
							: configurationId.length > 6
							? DesignSourceEnum.gallery
							: designSource === DesignSourceEnum.startFromSaved
							? DesignSourceEnum.startFromSaved
							: DesignSourceEnum.enterCode;

					if (rangeSettings) {
						statisticsService.sendInitPlanningSessionInsightEvent(
							vpcCode as string,
							vpcSource,
							settings,
							platform,
							rangeSettings.rangeName as SofaRangeName
						);

						dispatch(initiateInsights(true));

						if (isInUpptackaPlatform) {
							statisticsService.sendCustomInsightEvent(
								'in_upptacka_platform',
								settings,
								platform,
								rangeSettings.rangeName as SofaRangeName,
								IpexMomentEnum.makeItMe
							);
						}
					}
				}
				break;
			}
			case SET_APP_VIEW: {
				// Send analytics event (Go to checkout)
				if (action.value === 'summary-page') {
					if (rangeSettings) {
						statisticsService.sendCustomInsightEvent(
							'go_to_checkout',
							settings,
							platform,
							rangeSettings.rangeName as SofaRangeName,
							IpexMomentEnum.showMe
						);
					}
				}
				break;
			}
			case SET_SHOW_FUNCTIONS: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'click_action_button_function',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.showMe
					);
				}
				break;
			}
			case SET_SHOW_FUNCTION_SLEEP:
			case SET_SHOW_FUNCTION_STORAGE: {
				if (rangeSettings && !isScreensaverShown && action.track) {
					statisticsService.sendCustomInsightEvent(
						'click_action_button_function',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.showMe,
						{ type: action.type === SET_SHOW_FUNCTION_SLEEP ? 'sleep' : 'storage' }
					);
				}
				break;
			}
			case SET_SHOW_MEASUREMENTS: {
				// Send analytics event (Show measurements)
				if (rangeSettings && !isScreensaverShown) {
					statisticsService.sendCustomInsightEvent(
						'click_action_button_measurements',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.showMe,
						{ type: action.value ? 'enable' : 'disable' }
					);
				}
				break;
			}
			case SET_CURRENT_LIGHTBOX: {
				switch (action.value) {
					case 'product-intro-gallery':
						{
							if (rangeSettings && insightsInitiated && !isScreensaverShown) {
								statisticsService.sendCustomInsightEvent(
									'show_product_intro_gallery',
									settings,
									platform,
									rangeSettings.rangeName as SofaRangeName,
									IpexMomentEnum.showMe
								);
							}
						}
						break;
					case 'cover-picker':
						{
							if (rangeSettings) {
								statisticsService.sendCustomInsightEvent(
									'show_cover_picker',
									settings,
									platform,
									rangeSettings.rangeName as SofaRangeName,
									IpexMomentEnum.showMe,
									{ show: true }
								);
							}
						}
						break;
					case 'additional-cover-picker':
						{
							if (rangeSettings) {
								statisticsService.sendCustomInsightEvent(
									'click_additional_cover_card',
									settings,
									platform,
									rangeSettings.rangeName as SofaRangeName,
									IpexMomentEnum.showMe
								);
							}
						}
						break;
					case 'armrest-picker':
						{
							if (rangeSettings) {
								statisticsService.sendCustomInsightEvent(
									'open_armrest_selector',
									settings,
									platform,
									rangeSettings.rangeName as SofaRangeName,
									IpexMomentEnum.showMe
								);
							}
						}
						break;
					case 'save-for-later':
						{
							if (rangeSettings) {
								statisticsService.sendCustomInsightEvent(
									'open_save_for_later',
									settings,
									platform,
									rangeSettings.rangeName as SofaRangeName,
									IpexMomentEnum.showMe,
									{ show: true }
								);
							}
						}
						break;
					case 'additional-information':
						{
							if (rangeSettings && selectedArticle) {
								statisticsService.sendCustomInsightEvent(
									'show_additional_information',
									settings,
									platform,
									rangeSettings.rangeName as SofaRangeName,
									IpexMomentEnum.showMe,
									{ show: true, id: selectedArticle }
								);
							}
						}
						break;
					case 'none':
						{
							if (rangeSettings && prevState.application.currentLightbox === 'armrest-picker') {
								statisticsService.sendCustomInsightEvent(
									'close_armrest_selector',
									settings,
									platform,
									rangeSettings.rangeName as SofaRangeName,
									IpexMomentEnum.showMe
								);
							}
							if (rangeSettings && prevState.application.currentLightbox === 'cover-picker') {
								statisticsService.sendCustomInsightEvent(
									'show_cover_picker',
									settings,
									platform,
									rangeSettings.rangeName as SofaRangeName,
									IpexMomentEnum.showMe,
									{ show: false }
								);
							}
							if (
								rangeSettings &&
								prevState.application.currentLightbox === 'additional-information'
							) {
								statisticsService.sendCustomInsightEvent(
									'show_additional_information',
									settings,
									platform,
									rangeSettings.rangeName as SofaRangeName,
									IpexMomentEnum.showMe,
									{ show: false }
								);
							}
							if (rangeSettings && prevState.application.currentLightbox === 'save-for-later') {
								statisticsService.sendCustomInsightEvent(
									'open_save_for_later',
									settings,
									platform,
									rangeSettings.rangeName as SofaRangeName,
									IpexMomentEnum.showMe,
									{ show: false }
								);
							}
						}
						break;
					default:
						break;
				}
				break;
			}
			case SET_SELECTED_ARMREST: {
				if (
					rangeSettings &&
					action.value &&
					(nextState.application.currentLightbox === 'armrest-picker' ||
						nextState.scene.selectedSceneItem)
				) {
					if (action.value.modelId?.includes('WIDE')) {
						statisticsService.sendCustomInsightEvent(
							'select_wide_armrest',
							settings,
							platform,
							rangeSettings.rangeName as SofaRangeName,
							IpexMomentEnum.showMe
						);
					} else {
						statisticsService.sendCustomInsightEvent(
							'select_regular_armrest',
							settings,
							platform,
							rangeSettings.rangeName as SofaRangeName,
							IpexMomentEnum.showMe
						);
					}
				}
				break;
			}
			case SET_DRAG_ITEM_ID: {
				// Send analytics event (Drag in module swiper or scene)
				if (action.value !== null) {
					if (rangeSettings) {
						const overSceneItemId = api.getState().scene.overSceneItemId;

						if (!overSceneItemId && action.origin === 'scene') {
							break;
						}

						statisticsService.sendCustomInsightEvent(
							'module_dragged_in_swiper_or_scene',
							settings,
							platform,
							rangeSettings.rangeName as SofaRangeName,
							IpexMomentEnum.showMe,
							{
								id: action.value.toString(),
								origin: action.type === 'SET_DRAG_ITEM_ID' ? action.origin : null,
							}
						);
					}
				}
				break;
			}
			case SET_SELECTED_ITEM_ID: {
				if (action.value !== null) {
					if (rangeSettings) {
						const sceneItems = api.getState().scene.sceneItems;
						const products = api.getState().products.products;
						const models = api.getState().models.sofaModels;
						if (action.type === 'SET_SELECTED_ITEM_ID' && sceneItems.length === 0) {
							const product = products[action.value];
							if (product) {
								if (models[product.modelId].modelId === 'JATTEBO_CHAISE') {
									statisticsService.sendCustomInsightEvent(
										'chaise_added_to_empty_scene',
										settings,
										platform,
										rangeSettings.rangeName as SofaRangeName,
										IpexMomentEnum.makeItMe
									);
								}
							}
						}
					}
				}
				break;
			}
			case SWIPE_IN_MODULE_SWIPER: {
				// Send analytics event (Swipe in module swiper)
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'swipe_in_module_swiper',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.showMe
					);
				}
				break;
			}
			case SHOW_COVER_PRODUCT_DESCRIPTION: {
				if (action.value) {
					if (rangeSettings) {
						statisticsService.sendCustomInsightEvent(
							'click_show_cover_description',
							settings,
							platform,
							rangeSettings.rangeName as SofaRangeName,
							IpexMomentEnum.showMe
						);
					}
				}
				break;
			}
			case SET_SHOW_OPEN_DESIGN: {
				if (action.value) {
					if (rangeSettings) {
						statisticsService.sendCustomInsightEvent(
							'show_open_design',
							settings,
							platform,
							rangeSettings.rangeName as SofaRangeName,
							IpexMomentEnum.showMe
						);
					}
				}
				break;
			}
			case SET_ROTATION_BUTTON_CLICKED: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'click_action_button_rotation',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.showMe
					);
				}
				break;
			}
			case SET_SHOW_SELECT_ALL: {
				if (rangeSettings && action.value) {
					statisticsService.sendCustomInsightEvent(
						'click_action_button_select_all',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.showMe
					);
				}
				break;
			}
			case YMAL_PIP_LINK_CLICK: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'ymal_pip_link_click',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.showMe,
						{ articleId: action.value }
					);
				}
				break;
			}
			case SUMMARY_PAGE_TAB_CLICK: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'summary_page_tab_click',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.showMe,
						{ tabType: action.value }
					);
				}
				break;
			}
			case SET_ORIENTATION: {
				const hasOrientationChanged = previousOrientation !== action.value;
				if (
					rangeSettings &&
					hasOrientationChanged &&
					(deviceType === 'mobile' || deviceType === 'tablet') &&
					action.value
				) {
					statisticsService.sendCustomInsightEvent(
						'orientation_change',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.nonInteraction,
						{ orientation: action.value }
					);
				}
				break;
			}
			case SET_SELECTED_COVER: {
				if (rangeSettings && currentLightbox === 'cover-picker') {
					statisticsService.sendCustomInsightEvent(
						'cover_change',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.makeItMe,
						{ selectedCover: action.value.coverName, missingCover: missingCover }
					);
				}
				break;
			}
			case SET_COVER_ONLY: {
				if (rangeSettings) {
					if (currentLightbox === 'product-intro-gallery' && action.value) {
						statisticsService.sendCustomInsightEvent(
							'product_card_type_click',
							settings,
							platform,
							rangeSettings.rangeName as SofaRangeName,
							IpexMomentEnum.makeItMe,
							{ type: 'coverOnly' }
						);
					} else if (currentLightbox === 'none') {
						statisticsService.sendCustomInsightEvent(
							'cover_only_build_view',
							settings,
							platform,
							rangeSettings.rangeName as SofaRangeName,
							IpexMomentEnum.showMe,
							{ coverOnly: action.value }
						);
					}
				}
				break;
			}
			case SET_FROM_NAVIGATOR: {
				if (rangeSettings && !hasSentFromNavigator) {
					hasSentFromNavigator = true;
					setTimeout(() => {
						statisticsService.sendCustomInsightEvent(
							'opened_from_navigator',
							settings,
							platform,
							rangeSettings.rangeName as SofaRangeName,
							IpexMomentEnum.nonInteraction,
							{ sprId: predefinedConfigurationId, origin: action.value }
						);
					}, 5000);
				}
				break;
			}
			case SET_NO_ROTATION_ICON: {
				if (rangeSettings && action.value && !hasSentRotateEvent) {
					statisticsService.sendCustomInsightEvent(
						'rotate_scene',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.showMe
					);
					hasSentRotateEvent = true;
				}
				break;
			}
			case SET_SCENE_DATA: {
				if (rangeSettings) {
					const sceneItems = api.getState().scene.sceneItems;
					const products = api.getState().products.products;
					const models = api.getState().models.sofaModels;
					const enableShowFunctionSleep = sceneItems.some((item) =>
						ProductUtils.hasFeatureSleep(products, models, item.articleId, rangeSettings)
					);
					const enableShowFunctionStorage = sceneItems.some((item) =>
						ProductUtils.hasFeatureStorage(products, models, item.articleId, rangeSettings)
					);
					if (rangeSettings && enableShowFunctionSleep && !hasSentSleepEvent) {
						statisticsService.sendCustomInsightEvent(
							'action_button_function_enabled',
							settings,
							platform,
							rangeSettings.rangeName as SofaRangeName,
							IpexMomentEnum.nonInteraction,
							{
								type: 'sleep',
							}
						);
						hasSentSleepEvent = enableShowFunctionSleep;
					}
					if (rangeSettings && enableShowFunctionStorage && !hasSentStorageEvent) {
						statisticsService.sendCustomInsightEvent(
							'action_button_function_enabled',
							settings,
							platform,
							rangeSettings.rangeName as SofaRangeName,
							IpexMomentEnum.nonInteraction,
							{
								type: 'storage',
							}
						);
						hasSentStorageEvent = enableShowFunctionStorage;
					}
				}
				break;
			}
			case SET_PRODUCT_CARD_TYPE_CLICK: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'product_card_type_click',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.makeItMe,
						{ id: action.value.id, type: action.value.type }
					);
				}
				break;
			}
			case SHOW_SCREENSAVER: {
				if (platform === 'kiosk') {
					if (rangeSettings && action.value) {
						statisticsService.sendCustomInsightEvent(
							'show_screensaver',
							settings,
							platform,
							rangeSettings.rangeName as SofaRangeName,
							IpexMomentEnum.nonInteraction
						);
					}
					dispatch(initiateInsights(false));
				}
				break;
			}
			case SET_VISIBLE_MODAL: {
				if (rangeSettings && action.value) {
					const value = action.value as VisibleModal;
					switch (value.modal) {
						case 'zipAvailabilityCard': {
							statisticsService.sendCustomInsightEvent(
								'zip_availability_card_click',
								settings,
								platform,
								rangeSettings.rangeName as SofaRangeName,
								IpexMomentEnum.showMe
							);
							break;
						}
						case 'sortListCard': {
							statisticsService.sendCustomInsightEvent(
								'show_product_list_sorting',
								settings,
								platform,
								rangeSettings.rangeName as SofaRangeName,
								IpexMomentEnum.showMe
							);
						}
					}
				}
				break;
			}
			case SET_ZIP_CODE: {
				if (rangeSettings && insightsInitiated && action.value && action.track) {
					statisticsService.sendCustomInsightEvent(
						'zip_code_submitted',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.makeItMe,
						{ zipCode: action.value }
					);
				}
				break;
			}
			case SEND_SMS: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'send_sms',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.makeItMe
					);
				}
				break;
			}
			case SEND_EMAIL: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'send_email',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.makeItMe
					);
				}
				break;
			}
			case COPY_CODE: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'copy_code',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.makeItMe
					);
				}
				break;
			}
			case BACK_TO_SHARE_MENU: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'back_to_share_menu',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.makeItMe,
						{ from: action.value }
					);
				}
				break;
			}
			case SAVE_FOR_LATER_TAB_CLICK: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'save_for_later_tab_click',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.showMe,
						{ selectedTab: action.value }
					);
				}
				break;
			}
			case SET_SWIPER_EVENT_TYPE: {
				if (rangeSettings) {
					const payload = action.value.productId
						? { eventType: action.value.type, productId: action.value.productId }
						: { eventType: action.value.type };
					statisticsService.sendCustomInsightEvent(
						'set_swiper_event_type',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.showMe,
						payload
					);
				}
				break;
			}
			case REMOVE_SCENE_ITEM:
			case REMOVE_SCENE_ITEM_OUTSIDE_SCENE: {
				if (rangeSettings) {
					const type = action.type === 'REMOVE_SCENE_ITEM' ? 'click' : 'drag';
					const id =
						action.type === 'REMOVE_SCENE_ITEM'
							? action.item?.articleId
							: action.sceneObject?.articleId;
					statisticsService.sendCustomInsightEvent(
						'remove_scene_item',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.makeItMe,
						{ removeType: type, id: id || null }
					);
				}
				break;
			}
			case SET_STORE_ID: {
				if (rangeSettings && !isScreensaverShown) {
					statisticsService.sendCustomInsightEvent(
						'store_id_changed',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.makeItMe,
						{ storeId: action.value, isKiosk: platform === 'kiosk' }
					);
				}
				break;
			}
			case ADD_ADDITIONAL_COVER: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'click_add_additional_cover',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.makeItMe,
						{ selected: action.coverName }
					);
				}
				break;
			}
			case ADDITIONAL_COVER_ITEM_CLICKED: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'click_additional_cover_color',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.makeItMe,
						{ selected: action.value }
					);
				}
				break;
			}
			case ADDITIONAL_COVER_ITEM_REMOVED: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'picking_list_remove_item',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.makeItMe,
						{ article: action.article }
					);
				}
				break;
			}
			case ADDITIONAL_COVER_CLOSED: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'close_additional_cover',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.makeItMe
					);
				}
				break;
			}
			case NO_ARTICLES_IN_PLANNER: {
				// Initiate once we have downloaded settings from översätta, needed to send event in this case
				await statisticsService.initStatistics(
					settings,
					platform,
					toSofaRangeName(UrlService.getSofaRange()).toUpperCase() as SofaRangeName,
					(optimizelyClient) => {
						setFeature(optimizelyClient, features, dispatch);
					}
				);

				await statisticsService.sendCustomInsightEvent(
					'no_articles_in_planner',
					settings,
					platform,
					toSofaRangeName(UrlService.getSofaRange()).toUpperCase() as SofaRangeName,
					IpexMomentEnum.nonInteraction
				);
				alert(localeService.getTranslation('productsMissingErrorMessage'));
				break;
			}
			case SUMMARY_PAGE_EXPAND_PRODUCT: {
				if (rangeSettings) {
					await statisticsService.sendCustomInsightEvent(
						'summary_page_expand_product',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.showMe,
						{ id: action.id, expand: action.expand }
					);
				}
				break;
			}
			case THUMBNAIL_IMAGE_CHANGED: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'thumbnail_image_changed',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.showMe,
						{ imageUrl: action.value.imageUrl, origin: action.value.origin }
					);
				}
				break;
			}
			case SHOW_ADD_TO: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						action.value.origin === 'cart' ? 'open_add_to_cart' : 'open_wishlist',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.showMe,
						{ show: action.value.show }
					);
				}
				break;
			}
			case SET_CLOSE_GALLERY: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'back_to_planning',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.makeItMe,
						{ origin: action.value }
					);
				}
				break;
			}
			case SHOW_TIMEOUT_WARNING: {
				if (rangeSettings) {
					if (action.value.show) {
						statisticsService.sendCustomInsightEvent(
							'show_countdown_banner',
							settings,
							platform,
							rangeSettings.rangeName as SofaRangeName,
							IpexMomentEnum.nonInteraction
						);
					} else {
						if (action.value.sendEvent) {
							statisticsService.sendCustomInsightEvent(
								'close_countdown_banner',
								settings,
								platform,
								rangeSettings.rangeName as SofaRangeName,
								IpexMomentEnum.makeItMe
							);
						}
					}
				}
				break;
			}
			case SET_MISSING_DEEPLINK: {
				if (rangeSettings && action.value) {
					statisticsService.sendCustomInsightEvent(
						'missing_deeplink',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.nonInteraction,
						{ id: action.value }
					);
				}
				break;
			}
			case SET_UPPTACKA_SAVE_FAIL: {
				if (rangeSettings && action.value) {
					await statisticsService.sendCustomInsightEvent(
						'upptacka_save_fail',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.makeItMe,
						{ error: action.error }
					);
				}
				break;
			}
			case SHOW_POP_OVER: {
				if (rangeSettings && action.value.type) {
					const errorPopover: PopOverType[] = [
						PopOverEnum.UnfinishedSection,
						PopOverEnum.MissingCoverSection,
					];
					const infoPopover: PopOverType[] = [
						PopOverEnum.DisabledDrag,
						PopOverEnum.BuildLimit,
						PopOverEnum.MiddleModuleDrag,
						PopOverEnum.NoValidConnection,
						PopOverEnum.InvalidPlacement,
					];

					if (!hasSentPopoverEvent) {
						hasSentPopoverEvent = true;
						if (errorPopover.includes(action.value.type)) {
							statisticsService.sendCustomInsightEvent(
								'error_message_shown',
								settings,
								platform,
								rangeSettings.rangeName as SofaRangeName,
								IpexMomentEnum.nonInteraction,
								{ type: action.value.type }
							);
						} else if (infoPopover.includes(action.value.type)) {
							statisticsService.sendCustomInsightEvent(
								'info_message_shown',
								settings,
								platform,
								rangeSettings.rangeName as SofaRangeName,
								IpexMomentEnum.nonInteraction,
								{ type: action.value.type }
							);
						}

						// Timeout so that we don't send more than one event. Drag triggers multiple SHOW_POP_OVER
						setTimeout(() => {
							hasSentPopoverEvent = false;
						}, 5000);
					}
				}
				break;
			}
			case SORT_PRODUCT_LIST: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'sort_product_list',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.showMe,
						{ type: action.value }
					);
				}
				break;
			}
			case SET_SUMMARY_PAGE_DEFAULT_TAB: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'summary_page_default_tab',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.nonInteraction,
						{ type: action.value }
					);
				}
				break;
			}
			case STATISTICS_QR_PLANNING_ENABLED: {
				if (rangeSettings) {
					statisticsService.sendCustomInsightEvent(
						'qr_planning_enabled',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.nonInteraction
					);
				}
				break;
			}
			case MOVE_BACKREST_AS_ARMREST: {
				if (rangeSettings && action.item) {
					statisticsService.sendCustomInsightEvent(
						'rotate_jattebo_seat',
						settings,
						platform,
						rangeSettings.rangeName as SofaRangeName,
						IpexMomentEnum.makeItMe
					);
				}
				break;
			}
		}
		return result;
	};

export { statisticsMiddleware };
